import { createContext, PropsWithChildren, useContext, useRef, useState } from 'react'
import { ourUuid } from 'utils'
import { useMutation } from 'react-query'
import { GeneratePrompt } from 'types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { NewsletterSection, SectionsContext as ISectionsContext, SectionValues } from './Types'
import { generateNewsletterSections } from 'components/Wizards/generateNewsletterSections'

const SectionsContext = createContext<ISectionsContext>({
  abortGeneration: () => {},
  sections: [],
  generateSections: () => Promise.reject('Not implemented'),
  setSections: () => {},
  updateSection: () => {},
  updateSections: () => {},
  addSection: () => {},
  removeSection: () => {},
  removeImage: () => {},
  addImage: () => {},
  values: {
    topic: '',
    audience: '',
    call_to_action: '',
    avoid: '',
    filename: '',
    title: '',
  },
  isLoading: false,
})

export const SectionsProvider = ({ children }: PropsWithChildren) => {
  const workspaceId = useCurrentWorkspaceId()
  const [sections, setSections] = useState<NewsletterSection[]>([])
  const [values, setValues] = useState<SectionValues>()
  const { mutateAsync, isLoading } = useMutation(generateNewsletterSections)

  const abortController = useRef<AbortController | null>(null)
  if (abortController.current === null) abortController.current = new AbortController()

  const abortGeneration = () => {
    abortController.current?.abort()
    abortController.current = new AbortController()
  }

  const addSection = () => {
    setSections((oldSections) => {
      return [...oldSections, { id: ourUuid(), imageUrl: '', title: '' }]
    })
  }

  const removeSection = (sectionToRemove: NewsletterSection) => {
    setSections((sections) => {
      return sections.filter((section) => section.id !== sectionToRemove.id)
    })
  }

  const removeImage = (sectionToRemove: NewsletterSection) => {
    setSections((sections) => {
      return sections.map((section) => {
        if (section.id !== sectionToRemove.id) return section

        return {
          ...section,
          imageUrl: '',
        }
      })
    })
  }

  const addImage = (sectionToAddImage: NewsletterSection, imageUrl: string) => {
    setSections((sections) => {
      return sections.map((section) => {
        if (section.id === sectionToAddImage.id)
          return {
            ...sectionToAddImage,
            imageUrl,
          }

        return section
      })
    })
  }

  const updateSection = (sectionToUpdate: NewsletterSection) => {
    setSections((sections) => {
      return sections.map((section) => {
        if (section.id === sectionToUpdate.id) return sectionToUpdate

        return section
      })
    })
  }

  const updateSections = (newSections: NewsletterSection[]) => {
    setSections(newSections)
  }

  const generateSections = async (payload?: SectionValues, onComplete?: () => void) => {
    if (payload) setValues(payload)
    const generateValues = payload || values
    if (!generateValues) return Promise.reject('No values passed')

    const prompt: GeneratePrompt = { ...generateValues, generation_type: ChannelType.Newsletter }
    const data = await mutateAsync({
      payload: {
        prompt,
        workspaceId: workspaceId,
      },
      signal: abortController.current?.signal,
    })
    onComplete?.()
    const newSections: NewsletterSection[] = data.map((sectionData: string) => {
      return { id: ourUuid(), title: sectionData }
    })

    setSections((oldSections) => {
      return [...oldSections, ...newSections]
    })
    return sections
  }

  return (
    <SectionsContext.Provider
      value={{
        abortGeneration,
        sections,
        generateSections,
        setSections,
        updateSection,
        updateSections,
        addSection,
        removeSection,
        removeImage,
        addImage,
        isLoading,
        values,
      }}
    >
      {children}
    </SectionsContext.Provider>
  )
}

export const useSections = () => useContext(SectionsContext)
